import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect } from "react";
import { AuthContext } from "../AuthProvider";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";

// Codesmith access is per org, so the cache is keyed by org name to avoid
// leaking one org's flag into another while orgs load.
const CODESMITH_ENABLED_STORAGE_KEY_PREFIX = "sidebar-codesmith-enabled";
// Routes without an org segment (/usage, /settings, /support, /admin, ...)
// have no org to key the lookup with while auth and orgs load, so the last
// resolved org is kept as a pointer for those lookups. The server-side
// active org is what those routes resolve to, and this is the best local
// guess of it.
const LAST_ACTIVE_ORG_STORAGE_KEY = "sidebar-codesmith-last-org";
// The cache belongs to one account: without this marker, a later session on
// the same browser could adopt the previous account's flag on org-less
// routes via the last-org pointer until orgs resolve.
const CACHE_OWNER_STORAGE_KEY = "sidebar-codesmith-user";
function storageKeyForOrg(org: string) {
  return `${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:${org}`;
}
export function clearCodesmithLayoutCache() {
  if (typeof window === "undefined") return;
  try {
    const doomed = [LAST_ACTIVE_ORG_STORAGE_KEY, CACHE_OWNER_STORAGE_KEY];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:`)) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Local storage is an optimization; the resolved org setting remains authoritative.
  }
}

/**
 * Last resolved active org, for navigation targets on org-less routes while
 * orgs load. Paired with `getCachedCodesmithEnabled(undefined)`: when the
 * cached flag mounts Codesmith UI before `activeGithubOrg` resolves, this is
 * the org that cached flag belongs to, and org-less paths like
 * `/sessions/new` don't exist (sessions live under `/[org]/sessions`).
 */
export function getCachedLastActiveOrg(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
export function getCachedCodesmithEnabled(org: string | null | undefined): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const effectiveOrg = org || window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY);
    if (!effectiveOrg) return undefined;
    const value = window.localStorage.getItem(storageKeyForOrg(effectiveOrg));
    return value === null ? undefined : value === "true";
  } catch {
    return undefined;
  }
}

/**
 * Codesmith availability for the active org, falling back to the cached
 * last-known value while the org query is in flight so Codesmith-gated UI
 * (the rail row) doesn't pop in after every load.
 * `undefined` means genuinely unknown (first visit).
 */
export function useCodesmithEnabledWithCache() {
  const $ = _c(11);
  const {
    user
  } = useContext(AuthContext);
  const {
    activeGithubOrg,
    codesmithEnabled,
    codesmithEnabledKnown,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const isUnresolved = !orgsResolved;
  let t0;
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== codesmithEnabled || $[2] !== codesmithEnabledKnown || $[3] !== isUnresolved || $[4] !== user) {
    t0 = () => {
      if (!user) {
        return;
      }
      try {
        if (window.localStorage.getItem(CACHE_OWNER_STORAGE_KEY) !== String(user.id)) {
          clearCodesmithLayoutCache();
          window.localStorage.setItem(CACHE_OWNER_STORAGE_KEY, String(user.id));
        }
        if (isUnresolved) {
          return;
        }
        if (!activeGithubOrg) {
          window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
          return;
        }
        if (!codesmithEnabledKnown) {
          window.localStorage.removeItem(storageKeyForOrg(activeGithubOrg));
          if (window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) === activeGithubOrg) {
            window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
          }
          return;
        }
        window.localStorage.setItem(storageKeyForOrg(activeGithubOrg), String(codesmithEnabled));
        window.localStorage.setItem(LAST_ACTIVE_ORG_STORAGE_KEY, activeGithubOrg);
      } catch {}
    };
    t1 = [user, isUnresolved, activeGithubOrg, codesmithEnabled, codesmithEnabledKnown];
    $[0] = activeGithubOrg;
    $[1] = codesmithEnabled;
    $[2] = codesmithEnabledKnown;
    $[3] = isUnresolved;
    $[4] = user;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== activeGithubOrg || $[8] !== codesmithEnabled || $[9] !== isUnresolved) {
    t2 = isUnresolved ? getCachedCodesmithEnabled(activeGithubOrg) : codesmithEnabled;
    $[7] = activeGithubOrg;
    $[8] = codesmithEnabled;
    $[9] = isUnresolved;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}